import { shellStringResources } from '../../Resources/ShellStringResources';
import { Constants } from '../../shared/shared-constants';
import { format, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { IModel, IAttribute, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { ModelConstants } from '../model-constants';
import { Activity } from './activity';
import { ExecuteSSISPackageConnectionManagerParameterConfig } from './ssis.activity.connection-manager-parameter-config';
import { ExecuteSSISPackageParameterConfig } from './ssis.activity.parameter-config';
import { ExecuteSSISPackagePropertyOverrideConfig } from './ssis.activity.property-override-config';
import { PatternValidatorFactoryService } from '../../extensibility/services/validator-factory/pattern-validator-factory.service';
import { IntegerValidatorFactoryService } from '../../extensibility/services/validator-factory/integer-validator-factory.service';
import { NumberValidatorFactoryService } from '../../extensibility/services/validator-factory/number-validator-factory.service';
import { GlobalVariables } from '../../constants/globals';
import { isTridentInIframe } from '../../shared/utils/iframe.utils';

@Metamodel.model({
    name: ModelConstants.executeSSISPackageActivity,
    display: [
        format(shellStringResources.ModelName_ActivityTemplate, shellStringResources.FabricSsisActivityLabel),
        format(shellStringResources.ModelName_ActivitiesTemplate, shellStringResources.FabricSsisActivityLabel)
    ],
    referable: true
})
export abstract class SSISActivity extends Activity {
    public static readonly MODEL: IModel;

    public static readonly CONFIGURATION_FILE: IAttribute;
    public static readonly PROJECT_PATH: IAttribute;
    public static readonly PACKAGE_NAME: IAttribute;

    public static readonly PACKAGE_PASSWORD: IAttribute;
    public static readonly LOGGING_LEVEL: IAttribute;
    public static readonly LOG_PATH: IAttribute;

    public static readonly PARAMETERS: IAttribute;
    public static readonly CONNECTION_MANAGER_PARAMETERS: IAttribute;
    public static readonly PROPERTY_OVERRIDES: IAttribute;

    // CONST
    public static readonly PACKAGE_LOCATION_PROJECT_FILE = 'ProjectFile';
    public static readonly PACKAGE_LOCATION_PACKAGE_FILE = 'PackageFile';
    public static readonly CONFIG_EXTENSION = '.dtsconfig';
    public static readonly PROJECT_EXTENSION = '.ispac';
    public static readonly PACKAGE_EXTENSION = '.dtsx';

    @Metamodel.attribute({
        display: shellStringResources.ConfigurationFileLabel,
        association: 'CONFIGURATION_FILE',
        templatable: true,
        constraint: {
            validator: SSISActivity.createConfigurationFileValidator()
        }
    })
    public configFile?: string;

    public packageLocation: string;
    public fileType: 'PackageFile' | 'ProjectFile' = 'PackageFile';

    @Metamodel.attribute({
        display: shellStringResources.PasswordLabel,
        association: 'PACKAGE_PASSWORD',
        templatable: true
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public packagePassword?: any;

    @Metamodel.attribute({
        display: shellStringResources.SsisActivityProjectPathLabel,
        association: 'PROJECT_PATH',
        templatable: true,
        constraint: {
            validator: SSISActivity.createProjectPathValidator()
        }
    })
    public projectPath: string;

    @Metamodel.attribute({
        display: shellStringResources.SsisActivityPackageNameLabel,
        association: 'PACKAGE_NAME',
        templatable: true,
        constraint: {
            validator: SSISActivity.createPackageNameValidator()
        }
    })
    public packageName: string;

    @Metamodel.attribute({
        display: shellStringResources.SsisActivityLoggingLevelLabel,
        association: 'LOGGING_LEVEL',
        templatable: true
    })
    public loggingLevel?: 'None' | 'Basic' | 'Performance' | 'Verbose' = 'Basic';

    public useCustomizedLoggingLevel: boolean;

    @Metamodel.attribute({
        display: shellStringResources.LoggingPathLabel,
        association: 'LOG_PATH',
        templatable: true
    })
    public logPath: string;

    @Metamodel.attribute({
        association: 'PARAMETERS',
        constraint: {
            validator: SSISActivity.createExecuteSSISPackageParameterConfigValidator()
        }
    })
    public parameters: ExecuteSSISPackageParameterConfig[];

    @Metamodel.attribute({
        association: 'CONNECTION_MANAGER_PARAMETERS',
        constraint: {
            validator: SSISActivity.createExecuteSSISPackageConnectionManagerParameterConfig()
        }
    })
    public connectionManagerParameters: ExecuteSSISPackageConnectionManagerParameterConfig[];

    @Metamodel.attribute({
        association: 'PROPERTY_OVERRIDES',
        constraint: {
            validator: SSISActivity.createExecuteSSISPackagePropertyOverrideConfigValidator()
        }
    })
    public propertyOverrides: ExecuteSSISPackagePropertyOverrideConfig[];

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    protected static createExecuteSSISPackageParameterConfigValidator(): (activity?: {}, configs?: ExecuteSSISPackageParameterConfig[], violation?: IViolation) => IViolation[] | undefined {
        return (activity?: {}, configs?: ExecuteSSISPackageConnectionManagerParameterConfig[], violation?: IViolation): IViolation[] | undefined => {
            if (!configs) {
                return undefined;
            }
            const projectKeySet = new Set<string>();
            const packageKeySet = new Set<string>();
            const violations = new Array<IViolation>();
            configs.forEach((config, index) => {
                // validate name
                if (config.name == null || config.name === '') {
                    violations.push({
                        message: () => shellStringResources.Activity_Error_Configuration_InvalidName,
                        context: {
                            field: 'name',
                            index
                        }
                    });
                } else {
                    // only config with non-empty name will be take into account
                    const keySet = config.scope === ExecuteSSISPackageParameterConfig.SCOPE_PROJECT ? projectKeySet : packageKeySet;
                    if (keySet.has(config.name)) {
                        violations.push({
                            message: () => shellStringResources.ParameterConfiguration_Error_DuplicateName,
                            context: {
                                field: 'name',
                                index,
                                duplicate: true
                            }
                        });
                    }
                    keySet.add(config.name);
                }
                // check value
                if (config.required && !ExecuteSSISPackageParameterConfig.VALUE.value(config) && !ExecuteSSISPackageParameterConfig.VALUE.templateValue(config)) {
                    violations.push({
                        message: () => shellStringResources.SsisActivityRequiredParameterIsNotSet,
                        context: {
                            field: 'value',
                            index
                        }
                    });
                }
                const valueErrorMessage = SSISActivity.validateType(config);
                if (valueErrorMessage) {
                    violations.push({
                        message: () => valueErrorMessage,
                        context: {
                            field: 'value',
                            index
                        }
                    });
                }
            });
            return violations.length > 0 ? violations : undefined;
        };
    }

    protected static validateType(config: ExecuteSSISPackageConnectionManagerParameterConfig): string {
        if (!config.value) {
            return null;
        }
        if (typeof config.value === 'object') {
            return null;
        }
        if (config.sensitive && config.value === Constants.maskedCredential) {
            return null;
        }
        switch (config.type) {
            case 'Boolean':
                return SSISActivity.BOOLEAN_VALIDATOR(config.value);
            case 'Byte':
                return SSISActivity.BYTE_VALIDATOR(config.value);
            case 'Decimal':
                return SSISActivity.DECIMAL_VALIDATOR(config.value);
            case 'Double':
                return SSISActivity.DOUBLE_VALIDATOR(config.value);
            case 'Int16':
                return SSISActivity.INT16_VALIDATOR(config.value);
            case 'Int32':
                return SSISActivity.INT32_VALIDATOR(config.value);
            case 'Int64':
                return SSISActivity.INT64_VALIDATOR(config.value);
            case 'SByte':
                return SSISActivity.SBYTE_VALIDATOR(config.value);
            case 'UInt32':
                return SSISActivity.UINT32_VALIDATOR(config.value);
            case 'UInt64':
                return SSISActivity.UINT64_VALIDATOR(config.value);
            default:
                return null;
        }
    }

    private static createConfigurationFileValidator(): (activity?: SSISActivity, configFile?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: SSISActivity, configFile?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || (GlobalVariables.isTrident ? activity.fileType : activity.packageLocation) !== SSISActivity.PACKAGE_LOCATION_PACKAGE_FILE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (configFile && !configFile.toLowerCase().endsWith(SSISActivity.CONFIG_EXTENSION)) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(shellStringResources.SsisConfigPathValidatorMessage, shellStringResources.SsisActivityLabel, '\'' + activity.name + '\''),
                    attributes: [SSISActivity.CONFIGURATION_FILE]
                });
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createProjectPathValidator(): (activity?: SSISActivity, projectPath?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: SSISActivity, projectPath?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || (GlobalVariables.isTrident ? activity.fileType : activity.packageLocation) !== SSISActivity.PACKAGE_LOCATION_PROJECT_FILE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (!projectPath) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(shellStringResources.RequiredValidatorMessage, shellStringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                        shellStringResources.SsisActivityProjectPathLabel),
                    attributes: [SSISActivity.PROJECT_PATH]
                });
            } else if (!projectPath.toLowerCase().endsWith(SSISActivity.PROJECT_EXTENSION)) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(shellStringResources.SsisProjectPathValidatorMessage, shellStringResources.SsisActivityLabel, '\'' + activity.name + '\''),
                    attributes: [SSISActivity.PROJECT_PATH]
                });
            } else if (!projectPath.replace(/\\/g, '/').startsWith('//') && !isTridentInIframe()) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(shellStringResources.SsisFilePackagePathFormatValidatorMessage, shellStringResources.SsisActivityLabel, '\'' + activity.name + '\''),
                    attributes: [SSISActivity.PROJECT_PATH]
                });
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createPackageNameValidator(): (activity?: SSISActivity, packageName?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: SSISActivity, packageName?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || (GlobalVariables.isTrident ? activity.fileType : activity.packageLocation) !== SSISActivity.PACKAGE_LOCATION_PROJECT_FILE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (!packageName) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(shellStringResources.RequiredValidatorMessage, shellStringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                        shellStringResources.SsisActivityPackageNameLabel),
                    attributes: [SSISActivity.PACKAGE_NAME]
                });
            } else if (!packageName.toLowerCase().endsWith(SSISActivity.PACKAGE_EXTENSION)) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(shellStringResources.SsisPackageNameValidatorMessage, shellStringResources.SsisActivityLabel, '\'' + activity.name + '\''),
                    attributes: [SSISActivity.PACKAGE_NAME]
                });
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static readonly BOOLEAN_VALIDATOR = new PatternValidatorFactoryService().createValidator(/^(true|false|)$/i, shellStringResources.InvalidValue);
    private static readonly BYTE_VALIDATOR = new IntegerValidatorFactoryService().createValidator(0, 255, shellStringResources.InvalidValue);
    private static readonly DECIMAL_VALIDATOR = new NumberValidatorFactoryService().createValidator(undefined, undefined, shellStringResources.InvalidValue);
    private static readonly DOUBLE_VALIDATOR = new NumberValidatorFactoryService().createValidator(undefined, undefined, shellStringResources.InvalidValue);
    private static readonly INT16_VALIDATOR = new NumberValidatorFactoryService().createValidator(-32768, 32767, shellStringResources.InvalidValue);
    private static readonly INT32_VALIDATOR = new NumberValidatorFactoryService().createValidator(-2147483648, 2147483647, shellStringResources.InvalidValue);
    private static readonly INT64_VALIDATOR = new NumberValidatorFactoryService().createValidator(undefined, undefined, shellStringResources.InvalidValue);
    private static readonly SBYTE_VALIDATOR = new NumberValidatorFactoryService().createValidator(-128, 127, shellStringResources.InvalidValue);
    private static readonly UINT32_VALIDATOR = new NumberValidatorFactoryService().createValidator(0, 4294967295, shellStringResources.InvalidValue);
    private static readonly UINT64_VALIDATOR = new NumberValidatorFactoryService().createValidator(0, undefined, shellStringResources.InvalidValue);

    private static createExecuteSSISPackageConnectionManagerParameterConfig(): (activity?: {}, configs?: ExecuteSSISPackageConnectionManagerParameterConfig[], violation?: IViolation) =>
        IViolation[] | undefined {
        return (activity?: SSISActivity, configs?: ExecuteSSISPackageConnectionManagerParameterConfig[], violation?: IViolation): IViolation[] | undefined => {
            if (!configs) {
                return undefined;
            }
            const projectKeyMap = new Map<string, Set<string>>();
            const packageKeyMap = new Map<string, Set<string>>();
            const violations = new Array<IViolation>();
            configs.forEach((config, index) => {
                if (!config.name || !config.connectionManager) {
                    if (!config.name) {
                        violations.push({
                            message: () => shellStringResources.SsisActivityConnectionManagerPropertyCannotBeEmpty,
                            context: {
                                field: 'property',
                                index
                            }
                        });
                    }
                    if (!config.connectionManager) {
                        violations.push({
                            message: () => shellStringResources.SsisActivityConnectionManagerNameCannotBeEmpty,
                            context: {
                                field: 'connectionManager',
                                index
                            }
                        });
                    }
                } else {
                    const keyMap = config.scope === ExecuteSSISPackageParameterConfig.SCOPE_PROJECT ? projectKeyMap : packageKeyMap;
                    // check duplicate connection manager
                    if (config.index === 0 && keyMap.has(config.connectionManager)) {
                        violations.push({
                            message: () => shellStringResources.SsisActivityDuplicateConnectionManager,
                            context: {
                                field: 'connectionManager',
                                index,
                                duplicate: true
                            }
                        });
                    }
                    // check duplicate connection manager property
                    const keySet = keyMap.has(config.connectionManager) ? keyMap.get(config.connectionManager) : new Set<string>();
                    if (keySet.has(config.name)) {
                        violations.push({
                            message: () => shellStringResources.SsisActivityDuplicateConnectionManagerProperty,
                            context: {
                                field: 'property',
                                index,
                                duplicate: true
                            }
                        });
                    }
                    keySet.add(config.name);
                    keyMap.set(config.connectionManager, keySet);
                }
                // check value
                if (config.required && !ExecuteSSISPackageConnectionManagerParameterConfig.VALUE.value(config) && !ExecuteSSISPackageConnectionManagerParameterConfig.VALUE.templateValue(config)) {
                    violations.push({
                        message: () => shellStringResources.SsisActivityRequiredParameterIsNotSet,
                        context: {
                            field: 'value',
                            index
                        }
                    });
                }
                const valueErrorMessage = SSISActivity.validateType(config);
                if (valueErrorMessage) {
                    violations.push({
                        message: () => valueErrorMessage,
                        context: {
                            field: 'value',
                            index
                        }
                    });
                }
            });
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createExecuteSSISPackagePropertyOverrideConfigValidator(): (activity?: {}, configs?: ExecuteSSISPackagePropertyOverrideConfig[], violation?: IViolation) =>
        IViolation[] | undefined {
        return (activity?: {}, configs?: ExecuteSSISPackagePropertyOverrideConfig[], violation?: IViolation): IViolation[] | undefined => {
            if (!configs) {
                return undefined;
            }
            const keySet = new Set<string>();
            const violations = new Array<IViolation>();
            configs.forEach((config, index) => {
                // validate name
                if (config.name == null || config.name === '') {
                    violations.push({
                        message: () => shellStringResources.SsisActivityPropertyOverridePathCannotBeEmpty,
                        context: {
                            field: 'name',
                            index
                        }
                    });
                } else {
                    // only config with non-empty name will be take into account
                    if (keySet.has(config.name)) {
                        violations.push({
                            message: () => shellStringResources.SsisActivityDuplicatePropertyOverridePath,
                            context: {
                                field: 'name',
                                index,
                                duplicate: true
                            }
                        });
                    }
                    keySet.add(config.name);
                }
            });
            return violations.length > 0 ? violations : undefined;
        };
    }
}